import MenuButton, {
    MenuItem,
    MenuItems,
    MenuWrapper,
} from '../Button/MenuButton';
import { forwardRef, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import CheckboxIcon from './CheckboxIcon';
import { FiColumns } from 'react-icons/fi';
import { IconButton } from '../Button';
import { MdDragIndicator } from 'react-icons/md';
import { UnstyledButton } from 'utils/fragments';
import styled from 'styled-components';

const ColumnsMenuWrapper = styled(MenuWrapper)`
    align-self: flex-start;
`;
const ColumnsMenuItems = styled(MenuItems)`
    width: 20rem;
    border-radius: 0.5rem;
    max-height: unset;
`;
const ColumnsMenuItem = styled(MenuItem).attrs({
    className: '!py-1.5',
})`
    text-transform: capitalize;
    flex-grow: 1;
`;

function CheckedIcon() {
    return <CheckboxIcon checked={true} />;
}

function UncheckedIcon() {
    return <CheckboxIcon checked={false} />;
}

function isBeta(key) {
    switch (key) {
        case 'eligible_for_commission_posts_views_prc':
            return true;
        default:
            return false;
    }
}

function ColumnLabel({ children }) {
    return (
        <span
            css={`
                display: inline-flex;
                gap: 0.625rem;
                align-items: center;
            `}
        >
            {children}
        </span>
    );
}

const DragHandleWrapper = styled(UnstyledButton).attrs(({ $isDragging }) => ({
    style: {
        cursor: $isDragging ? 'grabbing' : 'grab',
    },
}))`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.5rem;
    position: absolute;
    right: 0;
    height: 100%;
`;

const DragIcon = styled(MdDragIndicator)`
    color: ${({ theme }) => theme.gray_500};
`;

const DragWrapper = styled.div`
    display: flex;
    align-items: center;
    position: relative;
`;

const DragHandle = forwardRef(({ $isDragging }, ref) => {
    return (
        <DragHandleWrapper $isDragging={$isDragging} ref={ref}>
            <DragIcon />
        </DragHandleWrapper>
    );
});

const ColumnsMenuItemType = 'ColumnsMenuItem';

const ColumnsMenuItemDraggable = forwardRef(
    ({ id, index, onMove, ...props }, menuItemRef) => {
        const draggableRef = useRef();
        const droppableRef = useRef();
        const [{ handlerId }, drop] = useDrop({
            accept: ColumnsMenuItemType,
            collect: (monitor) => ({ handlerId: monitor.getHandlerId() }),
            hover: (item, monitor) => {
                if (!draggableRef.current) {
                    return;
                }
                const dragIndex = item.index;
                const hoverIndex = index;
                // Don't replace items with themselves
                if (dragIndex === hoverIndex) {
                    return;
                }
                // Determine rectangle on screen
                const hoverBoundingRect =
                    draggableRef.current?.getBoundingClientRect();
                // Get vertical middle
                const hoverMiddleY =
                    (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
                // Determine mouse position
                const clientOffset = monitor.getClientOffset();
                // Get pixels to the top
                const hoverClientY = clientOffset.y - hoverBoundingRect.top;
                // Only perform the move when the mouse has crossed half of the items height
                // When dragging downwards, only move when the cursor is below 50%
                // When dragging upwards, only move when the cursor is above 50%
                // Dragging downwards
                if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                    return;
                }
                // Dragging upwards
                if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                    return;
                }
                // Time to actually perform the action
                onMove(dragIndex, hoverIndex);
                // Note: we're mutating the monitor item here!
                // Generally it's better to avoid mutations,
                // but it's good here for the sake of performance
                // to avoid expensive index searches.
                item.index = hoverIndex;
            },
        });

        const [{ isDragging }, drag, preview] = useDrag({
            type: ColumnsMenuItemType,
            item: () => ({ id, index }),
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
        });

        drag(draggableRef);
        drop(preview(droppableRef));
        return (
            <DragWrapper ref={droppableRef}>
                <ColumnsMenuItem
                    ref={menuItemRef}
                    data-handler-id={handlerId}
                    style={{ opacity: isDragging ? 0 : 1 }}
                    {...props}
                />
                <DragHandle isDragging={isDragging} ref={draggableRef} />
            </DragWrapper>
        );
    },
);

const defaultButtonProps = {
    as: IconButton,
    IconLeft: FiColumns,
    IconRight: null,
    children: 'Columns',
    size: 'default',
};

function ColumnsMenu({
    options,
    onToggle,
    onMove,
    buttonProps: buttonPropsProp,
    isDisabled,
    isLoading,
    menuAlign = 'right',
    mixpanelContext = 'ColumnsMenu',
    ...props
}) {
    const buttonProps = { ...defaultButtonProps, ...buttonPropsProp };
    const shapedOptions = options.map((option, index) => {
        const { key, label, checked } = option;
        return {
            key,
            index,
            id: key,
            children: <ColumnLabel isBeta={isBeta(key)}>{label}</ColumnLabel>,
            onClick: (e) => {
                e.preventDefault(); // Prevents the menu from closing
                onToggle(option);
            },
            onMove,
            IconLeft: checked ? CheckedIcon : UncheckedIcon,
            role: 'switch',
            'aria-checked': checked,
            mixpanelContext: 'ColumnsMenuItem',
            mixpanelMeta: {
                label,
                action: checked ? 'hide' : 'show',
            },
        };
    });

    return (
        <MenuButton
            buttonProps={{
                ...buttonProps,
                isDisabled,
                fetching: isLoading || false,
            }}
            options={shapedOptions}
            MenuWrapper={ColumnsMenuWrapper}
            MenuItems={ColumnsMenuItems}
            MenuItem={ColumnsMenuItemDraggable}
            menuAlign={menuAlign}
            mixpanelContext={mixpanelContext}
            {...props}
        />
    );
}

export default ColumnsMenu;
